
import { Component, ElementRef, forwardRef, Input, OnChanges, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TrimValueAccessorDirective } from '../../directives/trim-value-accessor/trim-value-accessor.directive';

export interface ChipOption {
  value: string;
  label: string;
  sublabel?: string;
  isExternal?: boolean;
  isAlreadyAssigned?: boolean;
}

@Component({
  selector: 'app-chip-select',
  standalone: true,
  imports: [FormsModule, TrimValueAccessorDirective],
  templateUrl: './chip-select.component.html',
  styleUrl: './chip-select.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipSelectComponent),
      multi: true,
    },
  ],
})
export class ChipSelectComponent implements ControlValueAccessor, OnChanges, OnInit {
  @Input() options: ChipOption[] = [];
  @Input() placeholder = 'Search...';
  @Input() addMoreText = 'Add more...';
  @Input() singleSelect = false;
  @Input() readonly = false;

  @ViewChild('chipInput') chipInputRef?: ElementRef<HTMLInputElement>;

  selectedValues: string[] = [];
  searchText = '';
  isOpen = false;

  // Memoized: recomputed only when options / selection / search text change, not
  // on every change-detection pass. A getter here re-filters the whole option
  // list on every CD tick, which — combined with the tour-guide tooltip
  // observer — pegs the main thread and makes the dropdown feel unresponsive.
  filteredOptions: ChipOption[] = [];

  private onChange: (val: string[]) => void = () => { /* empty */ };
  private onTouched: () => void = () => { /* empty */ };

  ngOnInit(): void {
    this.recomputeFiltered();
  }

  ngOnChanges(): void {
    this.selectedValues = [...this.selectedValues];
    this.recomputeFiltered();
  }

  trackByValue = (_: number, option: ChipOption): string => option.value;
  trackBySelf = (_: number, value: string): string => value;

  onSearchChange(value: string): void {
    this.searchText = value;
    this.recomputeFiltered();
  }

  private recomputeFiltered(): void {
    const q = this.searchText.toLowerCase();
    this.filteredOptions = this.options.filter(
      (o) =>
        !o.isAlreadyAssigned &&
        !this.selectedValues.includes(o.value) &&
        ((o.label ?? '').toLowerCase().includes(q) ||
          (o.sublabel?.toLowerCase().includes(q) ?? false))
    );
  }

  focusInput(): void {
    this.chipInputRef?.nativeElement.focus();
  }

  getLabelFor(value: string): string {
    return this.options.find((o) => String(o.value) === String(value))?.label ?? value;
  }

  isExternalValue(value: string): boolean {
    return this.options.find((o) => String(o.value) === String(value))?.isExternal ?? false;
  }

  select(option: ChipOption): void {
    this.selectedValues = this.singleSelect ? [option.value] : [...this.selectedValues, option.value];
    this.searchText = '';
    this.isOpen = false;
    this.recomputeFiltered();
    this.onChange(this.selectedValues);
  }

  remove(value: string, event: Event): void {
    event.stopPropagation();
    this.selectedValues = this.selectedValues.filter((v) => v !== value);
    this.recomputeFiltered();
    this.onChange(this.selectedValues);

    // stopPropagation() above blocks the wrapper's (click)="focusInput()", so
    // reopen the dropdown explicitly — removing a chip frees an option and the
    // user almost always wants to pick a replacement. Focus is deferred because
    // for singleSelect the input is only re-added to the DOM after this CD pass.
    if (!this.readonly) {
      this.isOpen = true;
      setTimeout(() => this.focusInput());
    }
  }

  onBlur(): void {
    setTimeout(() => { this.isOpen = false; }, 150);
    this.onTouched();
  }

  writeValue(value: string[]): void {
    this.selectedValues = value ?? [];
    this.recomputeFiltered();
  }

  registerOnChange(fn: (val: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
